import * as React from "react";
import { Card, CardContent, Container, Grid, Paper } from "@material-ui/core";
import { useState, useMemo } from "react";
import CockpitFilter from "./CockpitFilter";
import CockpitSkeleton from "./CockpitSkeleton";
import CockpitSvcUnsvcPnDifference from "./SvcUnsvcPnDifference/CockpitSvcUnsvcPnDifference";
import CockpitRequestedPnUnsvcPnDifference from "./RequestedPnUnsvcPnDifference/CockpitRequestedPnUnsvcPnDifference";
import CockpitRequestAircraft from "./RequestAircraft/CockpitRequestAircraft";
import ServiceableServiceLevelComponent from "./ServiceableServiceLevel/ServiceableServiceLevelComponent";
import UnserviceableServiceLevelComponent from "./UnserviceableServiceLevel/UnserviceableServiceLevelComponent";
import CockpitServiceableReturn from "./ServiceableReturn/CockpitServiceableReturn";
import CockpitCapability from "./Capability/CockpitCapability";
import CockpitOutOfStock from "./OutOfStock/CockpitOutOfStock";
import MonthlyServiceableServiceLevelComponent from "./MonthlyServiceableServiceLevel/MonthlyServiceableServiceLevelComponent";
import MonthlyServiceableRequestComponent from "./MonthlyServiceableRequest/MonthlyServiceableRequest";
import CockpitResponseTime from "./ResponseTime/CockpitResponseTime";
import CockpitRejectionRate from "./RejectionRate/CockpitRejectionRate";
import { useAuth, useVerifyPermission } from "@airborne/react-admin";
import CockpitFleetType from "./FleetType/CockpitFleetType";
import CockpitActiveAircraft from "./ActiveAircraft/CockpitActiveAircraft";
import CockpitActiveAircraftByPeriod from "./ActiveAircraft/CockpitActiveAircraftByPeriod";
import { ContractStatusEnum } from "../../Enums/ContractStatusEnum";
import moment from "moment";
import useAiAssistant from "../../Components/AiAssistant/useAiAssistant";

const Cockpit = props => {
  useVerifyPermission("customroutes", "cockpit.read");
  const { user } = useAuth();
  const [filterParams, setFilterParams] = useState({
    contractId__in: [0],
    contractId__in_actual: [],
    contractStatus: ContractStatusEnum.All,
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
    isLoading: true,
    contractList: [], // Contract listesi buradan erişilebilir olacak
  });

  const CreateFilter = useMemo(() => {
    // contractId__in_actual yüklenmeden API çağrısı yapılmasını engelle
    if (!filterParams.contractId__in_actual || filterParams.contractId__in_actual.length === 0) {
      return {
        contractId__in: [],
        createdDate__gte: moment(filterParams.startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        createdDate__lte: moment(filterParams.endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        contractStatus__eq: filterParams.contractStatus,
      };
    }

    return {
      contractId__in: filterParams.contractId__in_actual,
      createdDate__gte: moment(filterParams.startDate).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      createdDate__lte: moment(filterParams.endDate).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      contractStatus__eq: filterParams.contractStatus,
    };
  }, [filterParams.contractId__in_actual, filterParams.startDate, filterParams.endDate, filterParams.contractStatus]);

  useAiAssistant("cockpit", CreateFilter);

  const CreateFilterWithoutStatus = useMemo(() => {
    const { contractStatus__eq, ...restFilter } = CreateFilter;
    return restFilter;
  }, [CreateFilter]);

  const subArcs = [
    {
      limit: 40,
      color: "#5BE12C",
      showTick: true,
    },
    {
      limit: 60,
      color: "#F58B19",
      showTick: true,
    },
    {
      limit: 100,
      color: "#EA4228",
      showTick: true,
    },
  ];

  return (
    <>
      <CockpitFilter filterParams={filterParams} setFilterParams={setFilterParams} />

      {/* Contract listesi yüklenirken skeleton göster */}
      {(!filterParams.contractId__in_actual || filterParams.contractId__in_actual.length === 0) && <CockpitSkeleton />}

      {/* Contract listesi yüklendikten sonra gerçek componentleri göster */}
      {filterParams.contractId__in_actual && filterParams.contractId__in_actual.length > 0 && (
        <>
          <br />
          <Container maxWidth="xl">
            <Paper elevation={2} variant="outlined">
              <Grid container justifyContent="space-around" spacing={2}>
                <Grid item xs={4} container direction="column" alignItems="center">
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>
                      <CockpitResponseTime filterParams={CreateFilter} />
                    </CardContent>
                  </Card>
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>{<CockpitRequestAircraft filterParams={CreateFilter} />}</CardContent>
                  </Card>
                  {!user?.isCustomer && (
                    <Card style={{ marginBottom: 5, marginTop: 15 }}>
                      <CardContent>{<CockpitCapability filterParams={CreateFilter} />}</CardContent>
                    </Card>
                  )}
                </Grid>
                <Grid item xs={4} container direction="column" alignItems="center">
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>{<CockpitRejectionRate filterParams={CreateFilterWithoutStatus} />}</CardContent>
                  </Card>
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>
                      {<CockpitRequestedPnUnsvcPnDifference filterParams={CreateFilterWithoutStatus} />}
                    </CardContent>
                  </Card>

                  {!user?.isCustomer && (
                    <Card style={{ marginBottom: 5, marginTop: 15 }}>
                      <CardContent>{<CockpitOutOfStock filterParams={CreateFilterWithoutStatus} />}</CardContent>
                    </Card>
                  )}
                </Grid>
                <Grid item xs={4} container direction="column" alignItems="center">
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>
                      {<CockpitSvcUnsvcPnDifference filterParams={CreateFilterWithoutStatus} />}
                    </CardContent>
                  </Card>
                  <Card style={{ marginBottom: 5, marginTop: 15 }}>
                    <CardContent>{<CockpitServiceableReturn filterParams={CreateFilterWithoutStatus} />}</CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Container>
          <br />

          {filterParams?.contractId__in?.includes(0) === false && (
            <>
              <Container maxWidth="xl">
                <Paper elevation={2} variant="outlined">
                  <CardContent>
                    {
                      <ServiceableServiceLevelComponent
                        contractList={filterParams.contractList}
                        filterParams={CreateFilter}
                      />
                    }
                  </CardContent>
                </Paper>
              </Container>
              <br />{" "}
            </>
          )}
          {filterParams?.contractId__in?.includes(0) === false && (
            <>
              <Container maxWidth="xl">
                <Paper elevation={2} variant="outlined">
                  <CardContent>
                    {
                      <UnserviceableServiceLevelComponent
                        contractList={filterParams.contractList}
                        filterParams={CreateFilter}
                      />
                    }
                  </CardContent>
                </Paper>
              </Container>
              <br />
            </>
          )}

          {!user?.isCustomer && (
            <Container maxWidth="xl">
              <Paper elevation={2} variant="outlined">
                <CardContent>{<CockpitActiveAircraftByPeriod filterParams={CreateFilter} />}</CardContent>
              </Paper>
            </Container>
          )}
          <br />

          {!user?.isCustomer && (
            <Container maxWidth="xl">
              <Paper elevation={2} variant="outlined">
                <Grid container justifyContent="space-around" spacing={2}>
                  <Grid item xs={4} container direction="column" alignItems="center">
                    <Card style={{ marginBottom: 5, marginTop: 15 }}>
                      <CardContent>{<CockpitFleetType filterParams={CreateFilter} />}</CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4} container direction="column" alignItems="center">
                    <Card style={{ marginBottom: 5, marginTop: 15 }}>
                      <CardContent>{<CockpitActiveAircraft filterParams={CreateFilter} />}</CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Container>
          )}

          <Container maxWidth="xl">
            <Paper elevation={2} variant="outlined">
              <CardContent>{<MonthlyServiceableServiceLevelComponent filterParams={CreateFilter} />}</CardContent>
            </Paper>
          </Container>
          <br />

          <Container maxWidth="xl">
            <Paper elevation={2} variant="outlined">
              <CardContent>{<MonthlyServiceableRequestComponent filterParams={CreateFilter} />}</CardContent>
            </Paper>
          </Container>
          <br />
        </>
      )}
    </>
  );
};

export default Cockpit;
